import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { OfferCard, type Offer } from './OfferCard'
import { useStyles } from './OffersList.styles'
import { ButtonV2 } from '@labrav/react-components'
import { useSalesforceContext } from '@labrav/common'
import { setCloudMarketplaceProductListing } from '../../../modules/productListingFilter/action'
import { CloudMarketplace } from '@labrav/common'
import { useDispatch } from '../../../store'

export type { Offer }

export interface OffersListProps {
  offers: Offer[]
  loading?: boolean
  error?: string | null
  navigateToUrl?: (url: string) => void
  partnerId?: string
}

export const OffersList: React.FC<OffersListProps> = ({
  offers,
  loading = false,
  error = null,
  navigateToUrl,
  partnerId,
}) => {
  const { classes } = useStyles()

  const initialCollapsedSet = useMemo(() => {
    return new Set(
      offers.map((offer, index) => {
        const offerId =
          offer.labra_offer_id ||
          offer.id ||
          offer.private_offer_id ||
          `offer-${index}`
        return `offer-${index}-${offerId}`
      })
    )
  }, [offers])

  const [collapsedOffers, setCollapsedOffers] =
    useState<Set<string>>(initialCollapsedSet)

  useEffect(() => {
    const newIds = offers.map((offer, index) => {
      const offerId =
        offer.labra_offer_id ||
        offer.id ||
        offer.private_offer_id ||
        `offer-${index}`
      return `offer-${index}-${offerId}`
    })
    setCollapsedOffers(prev => {
      const updated = new Set(prev)
      newIds.forEach(id => {
        if (!updated.has(id)) {
          updated.add(id)
        }
      })
      return updated
    })
  }, [offers])

  const { actions } = useSalesforceContext()

  const toggleOfferCollapse = useCallback((offerId: string) => {
    setCollapsedOffers(prev => {
      const next = new Set(prev)
      if (next.has(offerId)) {
        next.delete(offerId)
      } else {
        next.add(offerId)
      }
      return next
    })
  }, [])

  const dispatch = useDispatch()
  const handleOfferClick = useCallback(
    async (
      offerId: string,
      productId: string,
      draft: boolean,
      cloudAccountId: string,
      cloudMarketplace: CloudMarketplace
    ) => {
      if (!navigateToUrl) {
        console.warn('navigateToUrl is not defined')
        return
      }

      if (!productId) {
        console.warn('productId is missing, cannot navigate')
        return
      }

      if (!offerId) {
        console.warn('offerId is missing, cannot navigate')
        return
      }

      // Construct full URL for Salesforce navigation
      // The URL should be relative to the Salesforce app base path
      if (typeof window !== 'undefined') {
        const baseUrl = window.location.origin
        if (cloudMarketplace) {
          await dispatch(
            setCloudMarketplaceProductListing(cloudMarketplace) as never
          )
        }
        const params = new URLSearchParams({
          from: 'opportunity-action-hub',
        })
        let fullPath = `${baseUrl}/external-apps/salesforce/partners/${cloudAccountId || partnerId}/flyout/products-listing/${productId}/private-offers/${offerId}/details?${params.toString()}`
        if (draft) {
          // If the offer is a draft, navigate to the edit page
          fullPath = `${baseUrl}/external-apps/salesforce/partners/${cloudAccountId || partnerId}/flyout/products-listing/${productId}/private-offers/${offerId}/edit?${params.toString()}`
          actions.openModal(fullPath, 'Edit Private Offer')
        } else {
          navigateToUrl(fullPath)
        }
      }
    },
    [navigateToUrl, partnerId]
  )

  const handleCreateClick = useCallback(() => {
    if (!partnerId) {
      console.error('partnerId is not available')
      return
    }
    try {
      if (typeof window !== 'undefined') {
        // Construct full URL for private offer creation modal
        const baseUrl = window.location.origin
        const modalUrl = `${baseUrl}/external-apps/salesforce/partners/${partnerId}/flyout/private-offer/create`

        // Open in Salesforce modal instead of navigating
        actions.openModal(modalUrl, 'Create Private Offer')
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `openModal to create offer failed for partner: ${partnerId}`,
        e
      )
    }
  }, [partnerId, actions])

  if (loading) {
    return (
      <div className={classes.cardContainer}>
        <div className={classes.metaLabel}>Loading offers...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={classes.cardContainer}>
        <div
          className={`${classes.metaLabel} ${classes.errorText}`}
          role="alert"
        >
          {error}
        </div>
      </div>
    )
  }

  if (offers.length === 0) {
    return (
      <div className={classes.emptyStateContainer}>
        <div className={classes.emptyStateText}>No offers yet.</div>
        <ButtonV2
          styleType="link"
          onClick={handleCreateClick}
          data-testid="add-a-user-button"
          smallVariant
          icon={<FontAwesomeIcon icon={faPlus} width="14px" height="14px" />}
        >
          Create
        </ButtonV2>
      </div>
    )
  }

  return (
    <div className={classes.cardContainer}>
      {offers.map((offer, index) => {
        const offerId =
          offer.labra_offer_id ||
          offer.id ||
          offer.private_offer_id ||
          `offer-${index}`
        const uniqueOfferId = `offer-${index}-${offerId}`
        const isCollapsed = collapsedOffers.has(uniqueOfferId)

        return (
          <OfferCard
            key={uniqueOfferId}
            offer={offer}
            index={index}
            isCollapsed={isCollapsed}
            onToggleCollapse={() => toggleOfferCollapse(uniqueOfferId)}
            onOfferClick={handleOfferClick}
          />
        )
      })}
    </div>
  )
}
